import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { CompanySetting } from '@/api/companies/api';
import { valuesEqual } from './change-tracking';
import { SETTINGS_CATEGORIES, type CategoryId } from './constants';

export type SettingChange = {
  key: string;
  categoryId: string;
  categoryTitle: string;
  label: string;
  oldDisplay: string;
  newDisplay: string;
  payload: NonNullable<CompanySetting>;
};

export type SearchableSetting = {
  key: string;
  label: string;
  valueText: string;
};

type CategoryHandlers = {
  reset: () => void;
  validate: () => boolean;
};

type CompanySettingsDraftContextValue = {
  changes: SettingChange[];
  hasUnsavedChanges: boolean;
  showChangesOnly: boolean;
  categoryFilter: string;
  searchQuery: string;
  setCategoryFilter: (id: string) => void;
  setSearchQuery: (query: string) => void;
  toggleShowChangesOnly: () => void;
  reportCategoryChanges: (categoryId: string, nextChanges: SettingChange[]) => void;
  reportSearchables: (categoryId: string, items: SearchableSetting[]) => void;
  registerCategory: (categoryId: string, handlers: CategoryHandlers) => () => void;
  resetAll: () => void;
  clearChanges: () => void;
  validateAll: (categoryIds?: string[]) => string | null;
  isCategoryVisible: (categoryId: string) => boolean;
  isSettingVisible: (categoryId: string, key: string) => boolean;
  failedCategoryId: string | null;
  setFailedCategoryId: (id: string | null) => void;
};

const CompanySettingsDraftContext = createContext<CompanySettingsDraftContextValue | null>(null);

const matchesSearch = (item: SearchableSetting, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [item.label, item.key, item.valueText].some(part =>
    String(part || '')
      .toLowerCase()
      .includes(needle)
  );
};

export const CompanySettingsDraftProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [changesByCategory, setChangesByCategory] = useState<
    Partial<Record<string, SettingChange[]>>
  >({});
  const [searchablesByCategory, setSearchablesByCategory] = useState<
    Partial<Record<string, SearchableSetting[]>>
  >({});
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showChangesOnly, setShowChangesOnly] = useState(false);
  const [failedCategoryId, setFailedCategoryId] = useState<string | null>(null);
  const handlersRef = useRef<Partial<Record<string, CategoryHandlers>>>({});

  const changes = useMemo(
    () => SETTINGS_CATEGORIES.flatMap(category => changesByCategory[category.id] || []),
    [changesByCategory]
  );

  useEffect(() => {
    if (changes.length === 0) {
      setShowChangesOnly(false);
    }
  }, [changes.length]);

  const reportCategoryChanges = useCallback((categoryId: string, nextChanges: SettingChange[]) => {
    setChangesByCategory(previous => {
      const current = previous[categoryId];
      if (
        current &&
        current.length === nextChanges.length &&
        current.every((item, index) => {
          const next = nextChanges[index]!;

          return (
            item.key === next.key &&
            item.oldDisplay === next.oldDisplay &&
            item.newDisplay === next.newDisplay &&
            valuesEqual(item.payload.value, next.payload.value)
          );
        })
      ) {
        return previous;
      }

      return { ...previous, [categoryId]: nextChanges };
    });
  }, []);

  const reportSearchables = useCallback((categoryId: string, items: SearchableSetting[]) => {
    setSearchablesByCategory(previous => ({ ...previous, [categoryId]: items }));
  }, []);

  const registerCategory = useCallback((categoryId: string, handlers: CategoryHandlers) => {
    handlersRef.current[categoryId] = handlers;

    return () => {
      delete handlersRef.current[categoryId];
    };
  }, []);

  const clearChanges = useCallback(() => {
    setChangesByCategory({});
    setShowChangesOnly(false);
    setFailedCategoryId(null);
  }, []);

  const toggleShowChangesOnly = useCallback(() => {
    setShowChangesOnly(current => {
      if (current) {
        return false;
      }

      setCategoryFilter('all');
      setSearchQuery('');
      return true;
    });
  }, []);

  const resetAll = useCallback(() => {
    Object.values(handlersRef.current).forEach(handlers => handlers?.reset());
    clearChanges();
  }, [clearChanges]);

  const validateAll = useCallback((categoryIds?: string[]) => {
    const categories = categoryIds?.length
      ? SETTINGS_CATEGORIES.filter(category => categoryIds.includes(category.id))
      : SETTINGS_CATEGORIES;

    for (const category of categories) {
      const handlers = handlersRef.current[category.id];
      if (!handlers) continue;

      if (!handlers.validate()) {
        setFailedCategoryId(category.id);
        return category.id;
      }
    }

    setFailedCategoryId(null);
    return null;
  }, []);

  const isSettingVisible = useCallback(
    (categoryId: string, key: string) => {
      if (categoryFilter !== 'all' && categoryFilter !== categoryId) return false;

      if (showChangesOnly) {
        const categoryChanges = changesByCategory[categoryId];
        if (!categoryChanges?.some(change => change.key === key)) return false;
      }

      const query = searchQuery.trim();
      if (!query) return true;

      const items = searchablesByCategory[categoryId] || [];
      const item = items.find(entry => entry.key === key);
      if (!item) {
        return SETTINGS_CATEGORIES.some(
          category =>
            category.id === (categoryId as CategoryId) &&
            category.title.toLowerCase().includes(query.toLowerCase())
        );
      }

      return matchesSearch(item, query);
    },
    [categoryFilter, changesByCategory, searchQuery, searchablesByCategory, showChangesOnly]
  );

  const isCategoryVisible = useCallback(
    (categoryId: string) => {
      if (categoryFilter !== 'all' && categoryFilter !== categoryId) return false;

      if (showChangesOnly && !changesByCategory[categoryId]?.length) return false;

      const query = searchQuery.trim();
      if (!query) return true;

      const items = searchablesByCategory[categoryId] || [];
      if (items.some(item => matchesSearch(item, query))) return true;

      const category = SETTINGS_CATEGORIES.find(entry => entry.id === categoryId);
      return Boolean(category?.title.toLowerCase().includes(query.toLowerCase()));
    },
    [categoryFilter, changesByCategory, searchQuery, searchablesByCategory, showChangesOnly]
  );

  const value = useMemo(
    () => ({
      changes,
      hasUnsavedChanges: changes.length > 0,
      showChangesOnly,
      categoryFilter,
      searchQuery,
      setCategoryFilter,
      setSearchQuery,
      toggleShowChangesOnly,
      reportCategoryChanges,
      reportSearchables,
      registerCategory,
      resetAll,
      clearChanges,
      validateAll,
      isCategoryVisible,
      isSettingVisible,
      failedCategoryId,
      setFailedCategoryId
    }),
    [
      changes,
      categoryFilter,
      searchQuery,
      showChangesOnly,
      reportCategoryChanges,
      reportSearchables,
      registerCategory,
      resetAll,
      clearChanges,
      validateAll,
      toggleShowChangesOnly,
      isCategoryVisible,
      isSettingVisible,
      failedCategoryId
    ]
  );

  return (
    <CompanySettingsDraftContext.Provider value={value}>
      {children}
    </CompanySettingsDraftContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider
export const useCompanySettingsDraft = () => {
  const context = useContext(CompanySettingsDraftContext);

  if (!context) {
    throw new Error('useCompanySettingsDraft must be used within CompanySettingsDraftProvider');
  }

  return context;
};
